import { Button } from 'antd';
import { EditIcon, TrashIcon } from '@/assets/icons';
import { cn } from '@/libs/utils';

/**
 * Format amount with thousands separator
 * @param {string} amount - Amount in format "123,45"
 * @returns {string} Formatted amount "1.234,56"
 */
const formatAmountForDisplay = (amount) => {
  if (!amount) return '0,00';

  // Split by comma to get integer and decimal parts
  const [integerPart, decimalPart = '00'] = amount.toString().split(',');

  // Add thousands separator (dot)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedInteger},${decimalPart}`;
};

/**
 * Payment Slips Table Columns
 *
 * Defines columns for displaying payment slips in SimpleTable
 *
 * @param {Array} paymentSlips - Array of payment slips for finding index
 * @param {Function} [handleEdit] - Function to handle edit action; without it
 *   (read-only) the actions column is left out
 * @param {Function} handleRemove - Function to handle delete action
 * @param {Function} t - Translation function
 * @returns {Array} Column definitions
 */
export const PaymentSlipsColumns = ({
  paymentSlips,
  handleEdit,
  handleRemove,
  t,
}) => {
  const columns = [
    {
      header: t('paymentSlipsType', { ns: 'forms' }),
      dataIndex: 'typeOfPaymentSlipForDisplay',
    },
    {
      header: t('paymentSlipsAmount', { ns: 'forms' }),
      dataIndex: 'amountForPayment',
      render: (amount) => `${formatAmountForDisplay(amount)} RSD`,
    },
    {
      header: t('paymentSlipsSendingDate', { ns: 'forms' }),
      dataIndex: 'dateOfSendingOfPayment',
      render: (date) => t('paymentSlipsDayOfMonth', { ns: 'forms', day: date }),
    },
    {
      header: t('paymentSlipsPayee', { ns: 'forms' }),
      dataIndex: 'payeeOfPayment',
    },
    {
      header: t('actions', { ns: 'tables' }),
      dataIndex: 'custom',
      render: (_, row) => (
        <div className="flex gap-2">
          <Button
            type="button"
            title={t('edit', { ns: 'forms' })}
            className={cn(
              'btn-default h-6 w-6 items-center justify-center rounded-md !p-0'
            )}
            onClick={() => handleEdit(paymentSlips.indexOf(row))}
            icon={<EditIcon className="w-[11px]" />}
          >
            <span className="sr-only">{t('edit', { ns: 'forms' })}</span>
          </Button>

          <Button
            type="danger"
            title={t('remove', { ns: 'forms' })}
            className={cn(
              'btn-danger h-6 w-6 items-center justify-center rounded-md !p-0'
            )}
            onClick={() => handleRemove(paymentSlips.indexOf(row))}
            icon={<TrashIcon className="w-[11px]" />}
          >
            <span className="sr-only">{t('remove', { ns: 'forms' })}</span>
          </Button>
        </div>
      ),
    },
  ];
  return handleEdit ? columns : columns.slice(0, -1);
};
